import React from 'react';
import styles from './Output.module.scss';
import { CanAffordHome, CanAffordPurchase } from 'src/services/finance.service';
import Selectors from 'src/utils/selectors';
import CanAfford from 'src/components/can-afford/CanAfford';
import PillLoader from 'src/components/atomic/atoms/pillLoader/PillLoader';
import MoreInfoTooltip from 'src/components/more-info-tooltip/MoreInfoTooltip';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import SyncLoader from 'src/components/sync-loader/SyncLoader';
import { LoaderType } from 'src/types/loader-type.enum';
import { TooltipPlacement } from 'src/types/tooltip-placement.enum';
import { WalkmePopupWrapper, WalkmeName } from 'src/components/atomic/organisms/walkme';

interface Props {
    title?: string;
    main: string;
    mainIsLoading?: boolean;
    canAfford?: CanAffordPurchase | CanAffordHome;
    subtext?: string;
    tooltipText?: string;
    className?: string;
    onClick?: Function;
    titleVariant?: TypographyVariants;
    titleComponent?: keyof JSX.IntrinsicElements;
    tooltipPlacement?: TooltipPlacement;
    tooltipClassName?: string;
}

const Output = ({
    className,
    title,
    tooltipText,
    onClick,
    main,
    mainIsLoading,
    subtext,
    canAfford,
    titleVariant,
    titleComponent,
    tooltipPlacement,
    tooltipClassName
}: Props): JSX.Element => {
    const mainTypography: JSX.Element = (
        <Typography
            variant={TypographyVariants.HEADING_XL}
            className={styles.main}
            component="p"
            aria-labelledBy={title ? 'outputTitle' : null}
            data-cy={Selectors.General.OUTPUT_CONTAINER_AMOUNT}
            aria-live="polite"
            aria-busy={mainIsLoading}
        >
            {main}
        </Typography>
    );
    return (
        <div className={`${styles.output} ${className}`} data-cy={Selectors.General.OUTPUT_CONTAINER}>
            {title && (
                <div className={styles.titleRow}>
                    <Typography
                        variant={titleVariant || TypographyVariants.HEADING_S}
                        className={!titleVariant && styles.title}
                        aria-label="outputTitle"
                        component={titleComponent ?? 'p'}
                    >
                        {title}
                    </Typography>
                    {tooltipText && (
                        <MoreInfoTooltip
                            body={tooltipText}
                            className={`${styles.moreInfoTooltip} ${tooltipClassName}`}
                            buttonAriaLabel={title}
                            onClick={onClick}
                            placement={tooltipPlacement}
                        />
                    )}
                </div>
            )}
            {mainIsLoading ? (
                <PillLoader />
            ) : (
                <SyncLoader type={LoaderType.PILL}>
                    <WalkmePopupWrapper walkmeName={WalkmeName.TRANSACTION_CLASSIFICATION} stepIndex={1}>
                        {mainTypography}
                    </WalkmePopupWrapper>
                </SyncLoader>
            )}
            {subtext && (
                <Typography variant={TypographyVariants.BODY} className={styles.subtext}>
                    {subtext}
                </Typography>
            )}
            {canAfford > -1 && <CanAfford canAfford={canAfford} />}
        </div>
    );
};

export default Output;
